import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { APIProvider, Map as GMap, Marker as GMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { MapPin, Plus, X, Clock, Search, LocateFixed, Upload, Download, Navigation, Flag, ShieldAlert, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  faFlag, faCity, faBuilding, faUtensils, faShower, faHeartPulse, faScaleBalanced,
  faMugHot, faBed, faShirt, faLanguage, faBriefcase, faPalette, faSuitcaseRolling,
  faEnvelope, faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../services/api';

const API = `${API_BASE_URL}/api/map`;

// Catégories : chaque type d'aide a une couleur distincte (marqueurs, légende, filtre).
export const CATEGORIES = [
  { key: 'intervention', label: 'Zone / point frontalier', color: '#d63e2a', fa: 'fa-flag' },
  { key: 'ville', label: 'Grande ville', color: '#2aad27', fa: 'fa-city' },
  { key: 'bureau', label: 'Bureau / coordination', color: '#2a6ed6', fa: 'fa-building' },
  { key: 'nourriture', label: 'Nourriture', color: '#f59e0b', fa: 'fa-utensils' },
  { key: 'douche', label: 'Douche / hygiène', color: '#06b6d4', fa: 'fa-shower' },
  { key: 'sante', label: 'Santé', color: '#e11d48', fa: 'fa-heart-pulse' },
  { key: 'juridique', label: 'Aide juridique / administrative', color: '#7c3aed', fa: 'fa-scale-balanced' },
  { key: 'accueil', label: 'Accueil de jour', color: '#0d9488', fa: 'fa-mug-hot' },
  { key: 'hebergement', label: 'Hébergement / logement', color: '#9333ea', fa: 'fa-bed' },
  { key: 'vetements', label: 'Vêtements', color: '#db2777', fa: 'fa-shirt' },
  { key: 'francais', label: 'Cours de français', color: '#4f46e5', fa: 'fa-language' },
  { key: 'travail', label: 'Emploi / travail', color: '#4d7c0f', fa: 'fa-briefcase' },
  { key: 'activites', label: 'Activités', color: '#ca8a04', fa: 'fa-palette' },
  { key: 'bagagerie', label: 'Bagagerie', color: '#78716c', fa: 'fa-suitcase-rolling' },
  { key: 'domiciliation', label: 'Domiciliation', color: '#b45309', fa: 'fa-envelope' },
  { key: 'autre', label: 'Autre', color: '#6b7280', fa: 'fa-circle-info' },
];

const CAT_BY_KEY = CATEGORIES.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});
const catInfo = (key) => CAT_BY_KEY[key] || CAT_BY_KEY.autre;

// Icône FontAwesome (tracé SVG) par catégorie, incrustée en blanc dans le marqueur.
const CAT_ICON = {
  intervention: faFlag, ville: faCity, bureau: faBuilding, nourriture: faUtensils,
  douche: faShower, sante: faHeartPulse, juridique: faScaleBalanced, accueil: faMugHot,
  hebergement: faBed, vetements: faShirt, francais: faLanguage, travail: faBriefcase,
  activites: faPalette, bagagerie: faSuitcaseRolling, domiciliation: faEnvelope, autre: faCircleInfo,
};

// Marqueur = goutte colorée (SVG data-URI) + icône blanche du type d'aide, pour Google Maps.
const _pinCache = {};
const pinFor = (cat) => {
  if (_pinCache[cat]) return _pinCache[cat];
  const { color } = catInfo(cat);
  const ic = CAT_ICON[cat] || CAT_ICON.autre;
  const [iw, ih, , , path] = ic.icon;               // [width, height, ligatures, unicode, pathData]
  const target = 15;                                 // taille de l'icône dans la tête du pin
  const s = target / ih;                             // échelle (hauteur 512 → 15px)
  const tx = 15 - (iw * s) / 2;                      // centrer horizontalement sur (15,15)
  const ty = 15 - (ih * s) / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="45" viewBox="0 0 30 42">`
    + `<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#ffffff" stroke-width="2"/>`
    + `<g transform="translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${s.toFixed(4)})"><path d="${path}" fill="#ffffff"/></g>`
    + `</svg>`;
  const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  _pinCache[cat] = url;
  return url;
};

const USER_PIN = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="7" fill="#2563eb" stroke="#ffffff" stroke-width="3"/></svg>`
);

// Distance Haversine (km)
const distanceKm = (a, b) => {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtDist = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);

// ---- Estimation « ouvert maintenant » à partir d'horaires en texte libre (français) ----
const DAY_WORDS = [
  ['lundi', 'lun'], ['mardi', 'mar'], ['mercredi', 'mer'], ['jeudi', 'jeu'],
  ['vendredi', 'ven'], ['samedi', 'sam'], ['dimanche', 'dim'],
];
const dayIndexNow = () => (new Date().getDay() + 6) % 7; // 0 = lundi
const minutesNow = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

const findDayIdx = (token) => DAY_WORDS.findIndex(([full, abbr]) => token.startsWith(abbr));

const parseDays = (s) => {
  const set = new Set();
  // plages "X au Y" / "X à Y"
  const rangeRe = /(lun\w*|mar\w*|mer\w*|jeu\w*|ven\w*|sam\w*|dim\w*)\s*(?:au|à|a|-)\s*(lun\w*|mar\w*|mer\w*|jeu\w*|ven\w*|sam\w*|dim\w*)/g;
  let m, matchedRange = false;
  while ((m = rangeRe.exec(s))) {
    const a = findDayIdx(m[1]), b = findDayIdx(m[2]);
    if (a >= 0 && b >= 0) { matchedRange = true; let i = a; while (true) { set.add(i); if (i === b) break; i = (i + 1) % 7; } }
  }
  // jours isolés
  DAY_WORDS.forEach(([full, abbr], idx) => { if (new RegExp(`\\b${abbr}`).test(s)) set.add(idx); });
  return set.size ? set : null;
};

const parseRanges = (s) => {
  const ranges = [];
  const re = /(\d{1,2})\s*[h:.]\s*(\d{0,2})\s*(?:-|à|a|–)\s*(\d{1,2})\s*[h:.]\s*(\d{0,2})/g;
  let m;
  while ((m = re.exec(s))) {
    const start = parseInt(m[1], 10) * 60 + (m[2] ? parseInt(m[2], 10) : 0);
    const end = parseInt(m[3], 10) * 60 + (m[4] ? parseInt(m[4], 10) : 0);
    if (!isNaN(start) && !isNaN(end) && end > start) ranges.push([start, end]);
  }
  return ranges;
};

// Renvoie 'open' | 'closed' | 'unknown'
const openStatus = (hours) => {
  if (!hours) return 'unknown';
  const s = hours.toLowerCase().replace(/–|—/g, '-');
  if (/sur rendez-vous|sur rdv|uniquement sur/.test(s) && !parseRanges(s).length) return 'unknown';
  const allWeek = /tous les jours|7\s*j\s*\/\s*7|7\s*\/\s*7/.test(s);
  const allDay = /24\s*h?\s*\/\s*24|24\s*\/\s*24|non-?stop/.test(s);
  let days = allWeek ? new Set([0, 1, 2, 3, 4, 5, 6]) : parseDays(s);
  const ranges = parseRanges(s);
  if (!days && ranges.length) days = new Set([0, 1, 2, 3, 4, 5, 6]);
  if (!days) return 'unknown';
  if (!days.has(dayIndexNow())) return 'closed';
  if (allDay) return 'open';
  if (!ranges.length) return 'unknown';
  const now = minutesNow();
  return ranges.some(([a, b]) => now >= a && now <= b) ? 'open' : 'closed';
};

const OPEN_LABEL = { open: 'Probablement ouvert', closed: 'Probablement fermé', unknown: 'Horaires à vérifier' };
const OPEN_COLOR = { open: '#16a34a', closed: '#9ca3af', unknown: '#b45309' };

// Filtre par public accueilli
const AUDIENCES = [
  { key: 'all', label: 'Tous les publics' },
  { key: 'femmes', label: 'Femmes', kw: ['femme'] },
  { key: 'familles', label: 'Familles', kw: ['famille', 'enfant', 'parent'] },
  { key: 'mineurs', label: 'Mineurs isolés / jeunes', kw: ['mineur', 'mna', 'jeune', '18-25', '16-25', 'moins de 18'] },
];
const isGenericPublic = (p) => { const s = (p || '').toLowerCase().trim(); return !s || /tous|toute|sans condition|sans distinction|public|mixte/.test(s); };
const matchAudience = (p, audKey) => {
  if (audKey === 'all') return true;
  const aud = AUDIENCES.find((a) => a.key === audKey);
  if (!aud) return true;
  const s = (p || '').toLowerCase();
  return aud.kw.some((k) => s.includes(k)) || isGenericPublic(p);
};

const NICE_CENTER = { lat: 43.7102, lng: 7.2620 };
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Contenu interne de la carte Google : marqueurs, fenêtre d'info, Street View, recentrage.
const MapContent = ({ locations, userPos, flyTo, isFounder, onReport, onEdit, onRemove }) => {
  const map = useMap();
  const [selected, setSelected] = useState(null);
  const didFitRef = useRef(false);

  // Cadrage initial : si aucune position utilisateur, on ajuste la carte pour englober
  // tous les lieux visibles (repli quand la géolocalisation est refusée/indisponible).
  useEffect(() => {
    if (!map || didFitRef.current) return;
    if (userPos) { didFitRef.current = true; return; } // la position utilisateur prend le dessus
    if (locations.length && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }));
      map.fitBounds(bounds, 48);
      didFitRef.current = true;
    }
  }, [map, locations, userPos]);

  // Recentre sur la position de l'utilisateur (au chargement) ou sur un lieu proche sélectionné.
  useEffect(() => {
    if (map && flyTo) { map.panTo({ lat: flyTo[0], lng: flyTo[1] }); map.setZoom(13); }
  }, [map, flyTo]);

  const openStreetView = (loc) => {
    if (!map) return;
    const sv = map.getStreetView();
    sv.setPosition({ lat: loc.lat, lng: loc.lng });
    sv.setPov({ heading: 0, pitch: 0 });
    sv.setZoom(0);
    sv.setVisible(true);
  };

  return (
    <>
      {userPos && (
        <GMarker position={{ lat: userPos[0], lng: userPos[1] }} icon={USER_PIN} title="Vous êtes ici" />
      )}
      {locations.map((loc) => (
        <GMarker
          key={loc.id}
          position={{ lat: loc.lat, lng: loc.lng }}
          icon={pinFor(loc.category)}
          title={loc.title}
          onClick={() => setSelected(loc)}
        />
      ))}
      {selected && (
        <InfoWindow
          position={{ lat: selected.lat, lng: selected.lng }}
          pixelOffset={[0, -36]}
          onCloseClick={() => setSelected(null)}
        >
          {(() => {
            const ci = catInfo(selected.category);
            const st = openStatus(selected.hours);
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`;
            const shareText = `${selected.title}${selected.address ? ' — ' + selected.address : (selected.city ? ' — ' + selected.city : '')}`
              + (selected.hours ? `\nHoraires : ${selected.hours}` : '')
              + `\nItinéraire : ${mapsUrl}\n\nPartagé via PADAID`;
            const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
            const sms = `sms:?&body=${encodeURIComponent(shareText)}`;
            const linkStyle = { fontSize: 12, color: '#fff', borderRadius: 6, padding: '3px 8px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 };
            return (
              <div style={{ minWidth: 210, maxWidth: 268, display: 'flex', flexDirection: 'column', gap: 5, color: '#111827' }} data-testid="gmap-infowindow">
                <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ color: ci.color }}><i className={`fa-solid ${ci.fa}`} /></span>
                  <span>{selected.title}</span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: ci.color }}>{ci.label}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: OPEN_COLOR[st] }}>● {OPEN_LABEL[st]}</div>
                {selected.address && <div style={{ fontSize: 12, color: '#374151' }}>📍 {selected.address}</div>}
                {selected.hours && <div style={{ fontSize: 12, color: '#4b5563' }}>🕒 {selected.hours}</div>}
                {selected.public && <div style={{ fontSize: 12, color: '#4b5563' }}>👥 {selected.public}</div>}
                {selected.description && <div style={{ fontSize: 12, color: '#4b5563' }}>{selected.description}</div>}

                <button
                  onClick={() => openStreetView(selected)}
                  style={{ ...linkStyle, background: '#111827', border: 'none', cursor: 'pointer', justifyContent: 'center', marginTop: 2 }}
                  data-testid="streetview-btn"
                >
                  <Eye size={13} /> Street View
                </button>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 2 }}>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, background: '#2563eb' }}><Navigation size={13} /> M'y rendre</a>
                  <a href={wa} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, background: '#25D366' }}>WhatsApp</a>
                  <a href={sms} style={{ ...linkStyle, background: '#0ea5e9' }}>SMS</a>
                </div>

                <button onClick={() => onReport(selected)} style={{ fontSize: 12, color: '#b45309', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0 0 0', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="report-error-btn">
                  <Flag size={12} /> Signaler une erreur
                </button>

                {isFounder && (
                  <div style={{ display: 'flex', gap: 12, paddingTop: 2 }}>
                    <button onClick={() => { onEdit(selected); setSelected(null); }} style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Pencil size={12} /> Modifier</button>
                    <button onClick={() => { onRemove(selected.id); setSelected(null); }} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Trash2 size={12} /> Supprimer</button>
                  </div>
                )}
              </div>
            );
          })()}
        </InfoWindow>
      )}
    </>
  );
};

// Carte Google Maps PADAID (centrée sur Nice) avec marqueurs et Street View.
const GoogleAidMap = ({ locations, userPos, flyTo, placing, isFounder, onPick, onReport, onEdit, onRemove }) => {
  if (!GMAPS_KEY) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center text-sm text-muted-foreground" data-testid="gmap-missing-key">
        Clé Google Maps non configurée (REACT_APP_GOOGLE_MAPS_API_KEY).
      </div>
    );
  }
  return (
    <APIProvider apiKey={GMAPS_KEY}>
      <GMap
        defaultCenter={NICE_CENTER}
        defaultZoom={12}
        style={{ width: '100%', height: '100%' }}
        gestureHandling="greedy"
        streetViewControl={true}
        mapTypeControl={false}
        fullscreenControl={true}
        clickableIcons={false}
        onClick={(e) => { if (placing && e.detail?.latLng) onPick(e.detail.latLng); }}
      >
        <MapContent
          locations={locations}
          userPos={userPos}
          flyTo={flyTo}
          isFounder={isFounder}
          onReport={onReport}
          onEdit={onEdit}
          onRemove={onRemove}
        />
      </GMap>
    </APIProvider>
  );
};

// Petit parseur CSV (gère les guillemets et les virgules dans les champs).
const parseCSV = (text) => {
  const rows = [];
  let row = [], field = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(field); rows.push(row); row = []; field = ''; }
    else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((v) => (v || '').trim() !== ''));
};

const CSV_TEMPLATE = 'title,category,address,city,country,hours,public,description,source,source_url\n"Exemple Resto du Coeur",nourriture,"1 rue de la Paix, 75002 Paris",Paris,France,"Lun-Ven 9h-12h",tous,"Distribution de repas","Restos du Coeur",https://www.restosducoeur.org\n';

const emptyForm = { title: '', description: '', lat: '', lng: '', category: 'intervention', address: '', city: '', hours: '', source: '', source_url: '' };

const Carte = () => {
  const { user, token } = useAuth();
  const isFounder = user?.user_type === 'fondateur';
  const [locations, setLocations] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState('');
  const [userPos, setUserPos] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [flyTo, setFlyTo] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [openNow, setOpenNow] = useState(false);
  const [audience, setAudience] = useState('all');
  const [reportFor, setReportFor] = useState(null);
  const [reportMsg, setReportMsg] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportOpenCount, setReportOpenCount] = useState(0);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    // Donnée publique: pas besoin des cookies (withCredentials:false évite tout ralentissement
    // côté serveur lié à la session) + timeout et une nouvelle tentative pour plus de robustesse.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.get(`${API}/locations`, { withCredentials: false, timeout: 15000 });
        setLocations(res.data.locations || []);
        return;
      } catch (e) {
        console.error(`Erreur chargement carte (tentative ${attempt + 1}/2)`, e);
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Au chargement : centrer la carte sur la position de l'utilisateur (peu importe d'où il se
  // connecte). Silencieux si refusé/indisponible → la carte cadre alors tous les lieux.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { const p = [pos.coords.latitude, pos.coords.longitude]; setUserPos(p); setFlyTo(p); },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const loadReports = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/reports`, authHeaders);
      setReports(res.data.reports || []);
      setReportOpenCount(res.data.open_count || 0);
    } catch (e) { /* non-fondateur ou erreur : ignoré */ }
  }, [token]);

  useEffect(() => { if (isFounder) loadReports(); }, [isFounder, loadReports]);

  const submitReport = async () => {
    if (!reportMsg.trim()) return;
    setReportSending(true);
    try {
      await axios.post(`${API}/locations/${reportFor.id}/report`, { message: reportMsg, reporter: reportEmail || null });
      setReportDone(true);
      setReportMsg(''); setReportEmail('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi du signalement');
    } finally {
      setReportSending(false);
    }
  };

  const resolveReport = async (id) => {
    try { await axios.post(`${API}/reports/${id}/resolve`, {}, authHeaders); await loadReports(); }
    catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };
  const deleteReport = async (id) => {
    if (!window.confirm('Supprimer ce signalement ?')) return;
    try { await axios.delete(`${API}/reports/${id}`, authHeaders); await loadReports(); }
    catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const present = useMemo(() => {
    const counts = {};
    locations.forEach((l) => { const k = catInfo(l.category).key; counts[k] = (counts[k] || 0) + 1; });
    return CATEGORIES.filter((c) => counts[c.key]).map((c) => ({ ...c, count: counts[c.key] }));
  }, [locations]);

  const isActive = (key) => active === null || active.has(key);
  const toggleCat = (key) => {
    setActive((prev) => {
      const base = prev === null ? new Set(present.map((c) => c.key)) : new Set(prev);
      if (base.has(key)) base.delete(key); else base.add(key);
      return base;
    });
  };
  const showAll = () => setActive(null);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return locations.filter((l) => {
      if (!isActive(catInfo(l.category).key)) return false;
      if (openNow && openStatus(l.hours) !== 'open') return false;
      if (!matchAudience(l.public, audience)) return false;
      if (!term) return true;
      const hay = [l.title, l.city, l.address, catInfo(l.category).label, l.description].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [locations, active, present, q, openNow, audience]);

  // Lieux les plus proches (quand la position est connue)
  const nearby = useMemo(() => {
    if (!userPos) return [];
    return visible
      .map((l) => ({ ...l, _d: distanceKm(userPos, [l.lat, l.lng]) }))
      .sort((a, b) => a._d - b._d)
      .slice(0, 8);
  }, [userPos, visible]);

  const locateMe = () => {
    setGeoError('');
    if (!navigator.geolocation) { setGeoError("La géolocalisation n'est pas disponible sur cet appareil."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { const p = [pos.coords.latitude, pos.coords.longitude]; setUserPos(p); setFlyTo(p); },
      () => setGeoError("Impossible d'obtenir votre position (autorisation refusée ?)."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); setPlacing(true); };
  const openEdit = (loc) => {
    setForm({
      title: loc.title, description: loc.description || '', lat: loc.lat, lng: loc.lng,
      category: catInfo(loc.category).key, address: loc.address || '', city: loc.city || '',
      hours: loc.hours || '', source: loc.source || '', source_url: loc.source_url || '',
    });
    setEditingId(loc.id); setShowForm(true); setPlacing(false);
  };
  const closeForm = () => { setShowForm(false); setPlacing(false); setEditingId(null); setForm(emptyForm); };

  const handlePick = (latlng) => {
    setForm((f) => ({ ...f, lat: Number(latlng.lat.toFixed(5)), lng: Number(latlng.lng.toFixed(5)) }));
    setPlacing(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || form.lat === '' || form.lng === '') { alert('Titre et coordonnées requis.'); return; }
    const body = {
      title: form.title, description: form.description, lat: Number(form.lat), lng: Number(form.lng),
      category: form.category, address: form.address || null, city: form.city || null,
      hours: form.hours || null, source: form.source || null, source_url: form.source_url || null,
    };
    try {
      if (editingId) await axios.put(`${API}/locations/${editingId}`, body, authHeaders);
      else await axios.post(`${API}/locations`, body, authHeaders);
      await load();
      closeForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce lieu ?')) return;
    try {
      await axios.delete(`${API}/locations/${id}`, authHeaders);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'modele_adresses_padaid.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const onCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(String(e.target.result || ''));
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImporting(true); setImportResult(null);
    try {
      const rows = parseCSV(csvText);
      if (rows.length < 2) { alert('CSV vide ou sans lignes de données (la 1re ligne doit être l\'en-tête).'); setImporting(false); return; }
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const items = rows.slice(1).map((r) => {
        const o = {};
        header.forEach((h, i) => { o[h] = (r[i] ?? '').trim(); });
        const item = {
          title: o.title, category: o.category || 'autre', address: o.address || null,
          city: o.city || null, country: o.country || 'France', hours: o.hours || null,
          public: o.public || null, description: o.description || '', source: o.source || null,
          source_url: o.source_url || null,
        };
        if (o.lat && o.lng) { item.lat = Number(o.lat); item.lng = Number(o.lng); }
        return item;
      }).filter((it) => it.title);
      const res = await axios.post(`${API}/locations/bulk`, { items }, authHeaders);
      setImportResult(res.data);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="carte-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <MapPin className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Carte des aides & zones d'intervention</h1>
              <p className="text-sm text-muted-foreground">Points d'aide (nourriture, douche, santé, hébergement, aide juridique…) et zones de présence PADAID.</p>
            </div>
          </div>
          {isFounder && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { setShowReports(true); loadReports(); }} className="flex items-center gap-2 relative" data-testid="reports-btn">
                <ShieldAlert className="h-4 w-4" /> Signalements
                {reportOpenCount > 0 && <span className="ml-1 rounded-full bg-red-500 text-white text-[10px] px-1.5 py-0.5" data-testid="reports-badge">{reportOpenCount}</span>}
              </Button>
              <Button variant="outline" onClick={() => { setShowImport(true); setImportResult(null); }} className="flex items-center gap-2" data-testid="import-csv-btn">
                <Upload className="h-4 w-4" /> Importer (CSV)
              </Button>
              <Button onClick={openCreate} className="flex items-center gap-2" data-testid="add-location-btn">
                <Plus className="h-4 w-4" /> Ajouter un lieu
              </Button>
            </div>
          )}
        </div>

        {/* Barre de recherche + géolocalisation */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Rechercher une ville, une adresse ou un type d'aide (ex. « Lyon », « douche »)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="map-search-input"
            />
          </div>
          <Button variant="outline" onClick={locateMe} className="flex items-center gap-2" data-testid="locate-me-btn">
            <LocateFixed className="h-4 w-4" /> Autour de moi
          </Button>
          <Button
            variant={openNow ? 'default' : 'outline'}
            onClick={() => setOpenNow((v) => !v)}
            className="flex items-center gap-2"
            data-testid="open-now-toggle"
            title="Afficher uniquement les lieux probablement ouverts (estimation d'après les horaires)"
          >
            <Clock className="h-4 w-4" /> Ouvert maintenant
          </Button>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm h-10"
            data-testid="audience-filter"
            title="Filtrer selon le public accueilli"
          >
            {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.key === 'all' ? 'Public : tous' : `Public : ${a.label}`}</option>)}
          </select>
          {q && <button onClick={() => setQ('')} className="text-xs underline text-primary" data-testid="clear-search">Effacer</button>}
        </div>
        {geoError && <div className="mb-2 text-xs text-red-600" data-testid="geo-error">{geoError}</div>}

        {isFounder && placing && (
          <div className="mb-3 text-sm text-primary bg-primary/10 rounded-lg px-4 py-2" data-testid="placing-hint">
            Cliquez sur la carte pour positionner le lieu.
          </div>
        )}

        {/* Légende cliquable = filtre par catégorie */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="map-legend">
          <span className="text-sm font-medium text-foreground mr-1">{visible.length} / {locations.length} lieux</span>
          {present.map((c) => (
            <button
              key={c.key}
              onClick={() => toggleCat(c.key)}
              data-testid={`legend-${c.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-all ${isActive(c.key) ? 'border-border bg-card' : 'border-transparent bg-muted/50 opacity-40'}`}
              title={isActive(c.key) ? 'Masquer' : 'Afficher'}
            >
              <span className="inline-flex items-center justify-center w-4 h-4" style={{ color: c.color }}><i className={`fa-solid ${c.fa}`} style={{ fontSize: 12 }} /></span>
              {c.label} <span className="text-muted-foreground">({c.count})</span>
            </button>
          ))}
          {active !== null && (
            <button onClick={showAll} className="text-xs underline text-primary" data-testid="legend-show-all">Tout afficher</button>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_280px] gap-4">
          <div className="rounded-xl overflow-hidden border border-border" style={{ height: '70vh' }} data-testid="map-container">
            <GoogleAidMap
              locations={visible}
              userPos={userPos}
              flyTo={flyTo}
              placing={placing}
              isFounder={isFounder}
              onPick={handlePick}
              onReport={(l) => { setReportFor(l); setReportDone(false); setReportMsg(''); setReportEmail(''); }}
              onEdit={openEdit}
              onRemove={remove}
            />
          </div>

          {/* Panneau « aides les plus proches » */}
          <div className="rounded-xl border border-border p-3 bg-card/50 overflow-y-auto" style={{ height: '70vh' }} data-testid="nearby-panel">
            {!userPos ? (
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Trouver l'aide la plus proche</p>
                <p>Cliquez sur « Autour de moi » pour afficher les points d'aide les plus proches de votre position, triés par distance.</p>
              </div>
            ) : (
              <>
                <p className="font-medium text-sm mb-2">Les plus proches</p>
                {nearby.length === 0 && <p className="text-sm text-muted-foreground">Aucun lieu visible. Ajustez les filtres.</p>}
                <ul className="space-y-2">
                  {nearby.map((l) => {
                    const ci = catInfo(l.category);
                    return (
                      <li key={l.id}>
                        <button
                          onClick={() => setFlyTo([l.lat, l.lng])}
                          className="w-full text-left rounded-lg border border-border px-3 py-2 hover:bg-muted/50 transition-colors"
                          data-testid={`nearby-item-${l.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium truncate">{l.title}</span>
                            <span className="text-xs font-semibold shrink-0" style={{ color: ci.color }}>{fmtDist(l._d)}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">{ci.label}{l.city ? ` · ${l.city}` : ''}</div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Modale ajout / édition */}
        {isFounder && showForm && (
          <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" data-testid="location-form-modal">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">{editingId ? 'Modifier le lieu' : 'Ajouter un lieu'}</h2>
                <button onClick={closeForm}><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={save} className="space-y-3">
                <Input placeholder="Titre (ex. Accueil de jour Cité)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="loc-title-input" />
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="loc-category-select">
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <Input placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="loc-address-input" />
                <Input placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="loc-city-input" />
                <Input placeholder="Horaires" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="loc-hours-input" />
                <textarea className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="loc-desc-input" />
                <div className="flex gap-2">
                  <Input type="number" step="any" placeholder="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="loc-lat-input" />
                  <Input type="number" step="any" placeholder="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="loc-lng-input" />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setPlacing(true)} className="w-full">Choisir sur la carte</Button>
                <Input placeholder="Source (ex. Watizat)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="loc-source-input" />
                <Input placeholder="Lien source (URL)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="loc-source-url-input" />
                <Button type="submit" className="w-full" data-testid="loc-save-btn">Enregistrer</Button>
              </form>
            </div>
          </div>
        )}

        {/* Modale import CSV */}
        {isFounder && showImport && (
          <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" data-testid="import-modal">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">Importer des adresses (CSV)</h2>
                <button onClick={() => setShowImport(false)}><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                En-tête attendu : <code className="text-[10px]">title, category, address, city, country, hours, public, description, source, source_url</code> (colonnes <code>lat</code>/<code>lng</code> optionnelles).
                Les coordonnées manquantes sont géocodées automatiquement (max 80 lignes/import). Une adresse déjà présente (même titre + ville + source) est ignorée.
              </p>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={downloadTemplate} className="text-xs inline-flex items-center gap-1 text-primary underline" data-testid="download-template-btn">
                  <Download className="h-3.5 w-3.5" /> Télécharger le modèle
                </button>
                <label className="text-xs inline-flex items-center gap-1 text-primary underline cursor-pointer">
                  <Upload className="h-3.5 w-3.5" /> Charger un fichier .csv
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onCsvFile(e.target.files?.[0])} data-testid="import-file-input" />
                </label>
              </div>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                rows={8}
                placeholder="Collez ici le contenu CSV…"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                data-testid="import-csv-textarea"
              />
              {importResult && (
                <div className="mt-3 text-sm rounded-lg bg-muted/50 p-3" data-testid="import-result">
                  <p className="text-green-600 font-medium">✅ {importResult.inserted} ajouté(s)</p>
                  <p className="text-muted-foreground text-xs">{importResult.skipped_existing} déjà présent(s) · {importResult.failed_count} géocodage(s) échoué(s)</p>
                  {importResult.failed_count > 0 && <p className="text-xs text-amber-700 mt-1">Non localisés : {importResult.failed.join(', ')}</p>}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowImport(false)}>Fermer</Button>
                <Button onClick={runImport} disabled={importing || !csvText.trim()} data-testid="run-import-btn">
                  {importing ? 'Import en cours…' : 'Importer'}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Modale signalement (visiteur) */}
        {reportFor && (
          <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" data-testid="report-modal">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><Flag className="h-4 w-4 text-amber-700" /> Signaler une erreur</h2>
                <button onClick={() => setReportFor(null)}><X className="h-5 w-5" /></button>
              </div>
              {reportDone ? (
                <div className="text-sm">
                  <p className="text-green-600 font-medium">✅ Merci ! Votre signalement a bien été envoyé.</p>
                  <p className="text-muted-foreground text-xs mt-1">Il nous aide à garder la carte à jour.</p>
                  <div className="flex justify-end mt-4"><Button onClick={() => setReportFor(null)}>Fermer</Button></div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">Lieu concerné : <span className="font-medium text-foreground">{reportFor.title}</span>{reportFor.city ? ` — ${reportFor.city}` : ''}</p>
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    rows={4}
                    placeholder="Que se passe-t-il ? (ex. lieu fermé, adresse erronée, horaires changés…)"
                    value={reportMsg}
                    onChange={(e) => setReportMsg(e.target.value)}
                    data-testid="report-message"
                  />
                  <Input className="mt-2" placeholder="Votre email (facultatif)" value={reportEmail} onChange={(e) => setReportEmail(e.target.value)} data-testid="report-email" />
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setReportFor(null)}>Annuler</Button>
                    <Button onClick={submitReport} disabled={reportSending || !reportMsg.trim()} data-testid="report-submit">{reportSending ? 'Envoi…' : 'Envoyer'}</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Modale signalements (fondateur) */}
        {isFounder && showReports && (
          <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4" data-testid="reports-modal">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Signalements ({reportOpenCount} ouvert{reportOpenCount > 1 ? 's' : ''})</h2>
                <button onClick={() => setShowReports(false)}><X className="h-5 w-5" /></button>
              </div>
              {reports.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun signalement pour le moment.</p>
              ) : (
                <ul className="space-y-2">
                  {reports.map((r) => (
                    <li key={r.id} className={`rounded-lg border p-3 ${r.status === 'open' ? 'border-amber-300 bg-amber-50/50' : 'border-border opacity-70'}`} data-testid={`report-item-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{r.location_title}{r.location_city ? ` — ${r.location_city}` : ''}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.status === 'open' ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>{r.status === 'open' ? 'ouvert' : 'traité'}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{r.message}</p>
                      {r.reporter && <p className="text-[11px] text-muted-foreground mt-0.5">Contact : {r.reporter}</p>}
                      <div className="flex gap-3 mt-2">
                        {r.status === 'open' && <button onClick={() => resolveReport(r.id)} className="text-xs text-green-600" data-testid={`resolve-${r.id}`}>Marquer traité</button>}
                        <button onClick={() => deleteReport(r.id)} className="text-xs text-red-600" data-testid={`delete-report-${r.id}`}>Supprimer</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end mt-4"><Button variant="outline" onClick={() => setShowReports(false)}>Fermer</Button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Carte;
